import { createRouter, createWebHistory } from 'vue-router'
import { updateMetaTags, defaultSEO } from '../utils/seo'
import HomeView from '../views/Home.vue'
import AboutView from '../views/About.vue'
import ServicesView from '../views/Services.vue'
import ProjectsView from '../views/Projects.vue'
import ProjectView from '../views/Project.vue'
import ContactView from '../views/Contact.vue'
import FAQView from '../views/FAQ.vue'
import TermsView from '../views/Terms.vue'
import PrivacyView from '../views/Privacy.vue'
import NotFoundView from '../views/NotFound.vue'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      title: 'Diginova - Digital Design & Web Development Agency | Swiss Excellence',
      description: 'Diginova blends digital and reality with expert branding, web design, and development services. Swiss digital agency creating stunning websites and digital experiences for forward-thinking businesses.',
      keywords: 'digital agency Switzerland, web design, web development, branding agency, UI/UX design, Swiss web agency, responsive design, digital marketing',
      canonical: 'https://diginova.ch/',
      ogImage: 'https://diginova.ch/assets/img/team-picture.jpg'
    }
  },
  {
    path: '/services',
    name: 'services',
    component: ServicesView,
    meta: {
      title: 'Our Services - Digital Solutions | Diginova',
      description: 'Comprehensive digital solutions including branding, web design, and development. Transform your ideas into digital excellence with our proven methodology and expert team.',
      keywords: 'digital services, branding services, web design services, web development, UI/UX design, digital transformation, Swiss digital agency',
      canonical: 'https://diginova.ch/services',
      ogImage: 'https://diginova.ch/assets/img/pexels-fauxels-3184433.jpg'
    }
  },
  {
    path: '/projects',
    name: 'projects',
    component: ProjectsView,
    meta: {
      title: 'Our Projects - Portfolio | Diginova',
      description: 'Explore our amazing projects and digital experiences. See how we\'ve helped businesses transform their digital presence with innovative design and development solutions.',
      keywords: 'portfolio, web design portfolio, digital projects, case studies, client work, Swiss web design examples',
      canonical: 'https://diginova.ch/projects',
      ogImage: 'https://diginova.ch/assets/img/pexels-fauxels-3184433.jpg'
    }
  },
  {
    path: '/projects/:slug',
    name: 'project',
    component: ProjectView,
    meta: {
      title: 'Project Details | Diginova',
      description: 'Detailed case study of our digital project. Learn about our process, technologies used, and the results achieved for our client.',
      keywords: 'project case study, web design project, digital solution, client success story',
      canonical: 'https://diginova.ch/projects/',
      ogImage: 'https://diginova.ch/assets/img/pexels-fauxels-3184433.jpg'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: AboutView,
    meta: {
      title: 'About Us - Swiss Digital Agency | Diginova',
      description: 'Learn about Diginova, a Swiss digital agency that believes in simple and timeless design. Meet our team of creative professionals dedicated to bringing your vision to life.',
      keywords: 'about Diginova, Swiss digital agency, web design team, digital agency Switzerland, creative team',
      canonical: 'https://diginova.ch/about',
      ogImage: 'https://diginova.ch/assets/img/team-picture.jpg'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: ContactView,
    meta: {
      title: 'Contact Us - Get In Touch | Diginova',
      description: 'Ready to bring your vision to life? Contact Diginova for a free consultation. We\'re here to help create something amazing together with our digital expertise.',
      keywords: 'contact Diginova, digital agency contact, web design consultation, Swiss web agency contact, get quote',
      canonical: 'https://diginova.ch/contact',
      ogImage: 'https://diginova.ch/assets/img/team-picture.jpg'
    }
  },
  {
    path: '/faq',
    name: 'faq',
    component: FAQView,
    meta: {
      title: 'Frequently Asked Questions | Diginova',
      description: 'Find answers to common questions about our digital services, process, pricing, and timelines. Get all the information you need before starting your project.',
      keywords: 'FAQ, digital agency questions, web design process, pricing, project timeline',
      canonical: 'https://diginova.ch/faq'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: TermsView,
    meta: {
      title: 'Terms of Service | Diginova',
      description: 'Read our terms of service and conditions for using Diginova digital agency services. Legal terms and conditions for our web design and development services.',
      keywords: 'terms of service, legal terms, conditions, Diginova terms',
      canonical: 'https://diginova.ch/terms',
      robots: 'noindex, follow'
    }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: PrivacyView,
    meta: {
      title: 'Privacy Policy | Diginova',
      description: 'Our privacy policy explains how we collect, use, and protect your personal information. Learn about data protection and privacy practices at Diginova.',
      keywords: 'privacy policy, data protection, GDPR, personal information, Diginova privacy',
      canonical: 'https://diginova.ch/privacy',
      robots: 'noindex, follow'
    }
  },
  { 
    path: '/404',
    name: 'NotFound',
    component: NotFoundView,
    meta: {
      title: 'Page Not Found | Diginova',
      description: 'The page you are looking for could not be found. Return to Diginova homepage to explore our digital agency services.',
      robots: 'noindex, nofollow'
    }
  },  
  { 
    path: '/:catchAll(.*)', 
    redirect: '/404'
  } 
]

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (e.g., back/forward navigation), restore it
    if (savedPosition) {
      return savedPosition
    }
    // Always scroll to top for any route navigation
    return { top: 0, left: 0 }
  }
})

// Global navigation guard for SEO meta tags
router.beforeEach((to, _from, next) => {
  // Use route meta data or fallback to default SEO
  const seoData = to.meta || defaultSEO
  
  // Update SEO meta tags
  updateMetaTags({
    title: seoData.title || defaultSEO.title,
    description: seoData.description || defaultSEO.description,
    keywords: seoData.keywords || defaultSEO.keywords,
    canonical: seoData.canonical || `https://diginova.ch${to.path}`,
    ogImage: seoData.ogImage || defaultSEO.ogImage,
    robots: seoData.robots || defaultSEO.robots
  })
  
  next()
})

export default router
